import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { attachScenarioCoaching } from '../../services/scenario-coaching';
import { kindInfo } from '../../models/scenario-demo-data';
import { GradeResult, Scenario, ScenarioStep, StepOutcome } from '../../models/scenario.model';

interface OutcomeRow extends StepOutcome { kindName: string; }

const STEP_LABELS = { narrative: 'Briefing', remediation: 'Coaching', debrief: 'Debrief', question: 'Question' };
// Bound repeated attempts and authored cycles.
const MAX_VISITS = 60;

@Component({
  selector: 'app-scenario-player',
  templateUrl: './scenario-player.component.html',
  styleUrls: ['./scenario-player.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScenarioPlayerComponent implements OnChanges {
  @Input() scenario: Scenario;
  @Input() startStepId: string | null = null;
  @Output() readonly closed = new EventEmitter<void>();

  current: ScenarioStep | null = null;
  stepLabel = '';
  typeLabel = '';
  continueLabel = 'Continue';
  score = 0;
  maxScore = 0;
  segments: boolean[] = [];
  outcomes: OutcomeRow[] = [];
  flaggedCount = 0;
  finished = false;
  /** Bumps on every visit so a retried step re-renders fresh. */
  visit = 0;
  visitKey: number[] = [];
  bodyParagraphs: string[] = [];

  private visited = new Set<string>();

  ngOnChanges(): void {
    this.restart();
  }

  restart(): void {
    attachScenarioCoaching(this.scenario);
    this.score = 0;
    this.maxScore = 0;
    this.outcomes = [];
    this.flaggedCount = 0;
    this.finished = false;
    this.visit = 0;
    this.visited.clear();
    const steps = this.scenario?.steps || [];
    this.go(steps.find(s => s.id === this.startStepId) || steps[0] || null);
  }

  continueNarrative(): void {
    this.go(this.find(this.current?.next));
  }

  onQuestionContinue(result: GradeResult): void {
    const step = this.current as ScenarioStep;
    this.score += result.earned;
    this.maxScore += result.max;
    if (result.flagged) { this.flaggedCount++; }
    this.outcomes = [...this.outcomes, {
      stepId: step.id, title: step.title, kind: step.question!.kind, result, kindName: kindInfo(step.question!.kind).name,
    }];
    if (result.correctness !== 'ok' && step.coaching?.after === 'retry') {
      this.go(step);
      return;
    }
    const target = result.correctness === 'ok' ? step.onCorrect || step.next : step.onIncorrect || (step.coaching ? step.onCorrect || step.next : step.next);
    this.go(this.find(target));
  }

  trackByIndex(i: number): number { return i; }

  private find(id: string | undefined): ScenarioStep | null {
    return (id && this.scenario.steps.find(s => s.id === id)) || null;
  }

  private go(step: ScenarioStep | null): void {
    this.visit++;
    if (!step || this.visit > MAX_VISITS) {
      this.current = null;
      this.finished = true;
      this.buildSegments();
      return;
    }
    this.current = step;
    this.visitKey = [this.visit];
    this.visited.add(step.id);
    const n = this.scenario.steps.indexOf(step) + 1;
    this.stepLabel = step.type === 'remediation' ? 'Coaching' : `Step ${String(n).padStart(2, '0')}`;
    this.typeLabel = STEP_LABELS[step.type];
    this.bodyParagraphs = (step.body || '').split(/\n+/).filter(Boolean);
    const hasNext = step.type === 'question' ? !!(step.next || step.onCorrect || step.onIncorrect) : !!step.next;
    this.continueLabel = hasNext ? 'Continue' : 'Finish';
    this.buildSegments();
  }

  private buildSegments(): void {
    const main = this.scenario.steps.filter(s => s.type !== 'remediation');
    this.segments = main.map(s => this.finished || this.visited.has(s.id));
  }
}
